type SessionPreferenceConfig<T> = {
    key: string;
    isEmpty: (value: T) => boolean;
    isValid?: (value: unknown) => value is T;
};

type SessionPreference<T> = {
    read: (projectId: string, defaultValue: T) => T;
    persist: (projectId: string, value: T) => void;
    clear: () => void;
};

function readSessionPreference<T>({ key, isEmpty, isValid }: SessionPreferenceConfig<T>, projectId: string, defaultValue: T): T {
    try {
        const stored = sessionStorage.getItem(key);
        if (!stored) {
            return defaultValue;
        }
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        const value = parsed[projectId];
        if (value === undefined) {
            return defaultValue;
        }
        if (isValid && !isValid(value)) {
            return defaultValue;
        }
        const typedValue = value as T;
        if (isEmpty(typedValue)) {
            return defaultValue;
        }
        return typedValue;
    } catch {
        return defaultValue;
    }
}

function persistSessionPreference<T>({ key, isEmpty }: SessionPreferenceConfig<T>, projectId: string, value: T): void {
    try {
        const stored = sessionStorage.getItem(key);
        const current: Record<string, unknown> = stored ? (JSON.parse(stored) as Record<string, unknown>) : {};
        if (!isEmpty(value)) {
            current[projectId] = value;
        } else {
            delete current[projectId];
        }
        const hasAny = Object.keys(current).length > 0;
        if (hasAny) {
            sessionStorage.setItem(key, JSON.stringify(current));
        } else {
            sessionStorage.removeItem(key);
        }
    } catch {
        // sessionStorage indisponivel (modo privado, quota) — preferencia efemera
    }
}

function clearSessionPreference(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // sessionStorage indisponivel — nada a fazer
    }
}

export function createSessionPreference<T>(key: string, isEmpty: (value: T) => boolean, isValid?: (value: unknown) => value is T): SessionPreference<T> {
    const config: SessionPreferenceConfig<T> = { key, isEmpty, isValid };
    return {
        read: (projectId, defaultValue) => readSessionPreference(config, projectId, defaultValue),
        persist: (projectId, value) => persistSessionPreference(config, projectId, value),
        clear: () => clearSessionPreference(key)
    };
}
